import {
  CATEGORIES_PAGE_CONTENT_QUERY,
  ERROR_QUERY,
  POSTERS_BY_CATEGORY_QUERY,
  TOTAL_POSTERS_COUNT_QUERY,
} from "~/sanity/queries";
import type { LoaderFunctionArgs } from "@remix-run/node";
import { type Poster } from "~/types";
import { motion } from "framer-motion";
import { dataset, projectId } from "~/sanity/projectDetails";
import urlBuilder from "@sanity/image-url";
import { Image as OImage } from "~/components";

import { json } from "@remix-run/node";

import { loadQuery } from "~/sanity/loader.server";
import { loadQueryOptions } from "~/sanity/loadQueryOptions.server";

import {
  isRouteErrorResponse,
  Link,
  useLoaderData,
  useRouteError,
} from "@remix-run/react";

import {
  getAlternateLinks,
  getPageMeta,
  getWebsiteLDJsonMeta,
  mergeMeta,
  useModalStore,
} from "~/utils";
import { useEffect, useRef, useState } from "react";
import { NotFoundPage } from "~/components";
import { Button } from "~/components/ui/button";
import { AspectRatios } from "~/utils/constants";
import { AspectRatio } from "~/components/ui/aspect-ratio";
import { RootLoader } from "~/root";
import { CategoriesPageContent } from "~/types/categoriesPageContent";
import { cn } from "~/lib/utils";

const ITEMS_PER_PAGE = 150;

export const meta = mergeMeta<typeof loader, { root: RootLoader }>(
  ({ data, location }) => {
    if (!data) return [];

    const { seo } = data;

    return [
      ...getPageMeta({
        title: seo.title,
        description: seo.description,
      }),
      getWebsiteLDJsonMeta({
        description: seo.structuredDataWebsiteDescription,
      }),
      ...getAlternateLinks(location.pathname),
    ];
  }
);

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const { options, market } = await loadQueryOptions(
    request.headers,
    request.url
  );

  if (!market) {
    const errorPageData = await loadQuery(
      ERROR_QUERY,
      { locale: "se" },
      options
    ).then((res) => ({
      ...res,
      data: res.data ? res.data : null,
    }));

    throw json(errorPageData.data, { status: 404 });
  }

  const url = new URL(request.url);
  const currentPage = parseInt(url.searchParams.get("page") || "1", 10);
  const index = currentPage - 1;
  const skip = index * ITEMS_PER_PAGE;

  const postersInitial = await loadQuery<Poster[]>(
    POSTERS_BY_CATEGORY_QUERY,
    {
      category: params.category || "posters",
      limit: ITEMS_PER_PAGE,
      skip: skip,
      locale: market.locale || market.languageCode,
    },
    options
  );

  const categoriesPageContent = await loadQuery<CategoriesPageContent>(
    CATEGORIES_PAGE_CONTENT_QUERY,
    {
      locale: market.locale,
    },
    options
  );

  const totalPostersCount = await loadQuery(
    TOTAL_POSTERS_COUNT_QUERY,
    {
      category: params.category || "posters",
    },
    options
  );

  if (!postersInitial?.data.length || !categoriesPageContent?.data) {
    throw json({ locale: market.locale }, { status: 404 });
  }

  const categoryObject = categoriesPageContent.data.categories.find(
    (c) => c.value.toLowerCase() === params.category?.toLowerCase()
  );

  return {
    posters: postersInitial.data,
    query: POSTERS_BY_CATEGORY_QUERY,
    currentPage,
    params,
    market,
    categoriesPageContent: categoriesPageContent.data,
    seo: {
      title: categoryObject?.seoLabel
        ? `${categoryObject.seoLabel} | NordicPosterStore`
        : `${categoryObject?.label} | NordicPosterStore`,
      ...categoriesPageContent.data.seo,
    },
  };
};

export default function Index() {
  const { posters, categoriesPageContent, market, params } =
    useLoaderData<typeof loader>();

  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const modals = useModalStore((s) => s.modals);
  const containerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (activeRef.current && containerRef.current) {
      const container = containerRef.current;
      const activeElement = activeRef.current;
      container.scrollTo({
        left:
          activeElement.offsetLeft -
          container.offsetWidth / 2 +
          activeElement.offsetWidth / 2,
        behavior: "smooth",
      });
    }
  }, [params.category]);

  return (
    <div>
      <motion.div
        variants={{
          visible: { top: 0 },
          hidden: { top: 56 },
        }}
        animate={!!modals["topNav"] ? "visible" : "hidden"}
        transition={{
          duration: 0.35,
          ease: [0.19, 1, 0.22, 1],
        }}
        className="w-full md:mx-auto overflow-x-auto scroll-snap-type-x mandatory no-scrollbar border-b-[0.5px] border-black sticky bg-white z-10 "
      >
        <div
          ref={containerRef}
          className="!pl-3 !md:pr-4 !md:pl-6 flex space-x-2 justify-between md:justify-center overflow-x-auto no-scrollbar p-4"
        >
          {categoriesPageContent.categories
            .filter((category) => !category.hide)
            .map((category) => {
              const isActive = category.value === params.category;
              return (
                <Button
                  key={category.id}
                  variant="ghost"
                  className={cn(
                    "rounded-[2px] hover:bg-gray-200 px-3 py-2",
                    isActive ? "font-semibold bg-gray-100" : ""
                  )}
                  size="sm"
                  asChild
                  ref={isActive ? activeRef : null}
                >
                  <Link
                    prefetch="intent"
                    to={`/${market.locale}/${category.value}`}
                  >
                    {category.label}
                  </Link>
                </Button>
              );
            })}
        </div>
      </motion.div>

      <section className="px-2 my-4 md:my-6">
        <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {posters?.map((poster) => {
            const imgSrcUrl =
              poster.displayImage &&
              urlBuilder({ projectId, dataset })
                .image(
                  hoveredItemId === poster.id && poster.hoverImage
                    ? poster.hoverImage
                    : poster.displayImage
                )
                .width(800)
                .fit("max")
                .auto("format")
                .url();

            function prefetchImage() {
              let prefImage = new Image();
              prefImage.src = imgSrcUrl;
            }

            return (
              <li key={poster.id} className="md:col-span-1">
                <Link
                  prefetch="intent"
                  onMouseEnter={prefetchImage}
                  onFocus={prefetchImage}
                  className="cursor-pointer p-0 bg-transparent"
                  to={`/${market.locale}/posters/${poster.slug.current}`}
                >
                  <article
                    className="grid grid-flow-row items-start p-2 shadow-black transition-all duration-300 "
                    onMouseEnter={() => setHoveredItemId(poster.id)}
                    onMouseLeave={() => setHoveredItemId(null)}
                  >
                    <AspectRatio
                      ratio={AspectRatios.tall}
                      className="overflow-hidden"
                    >
                      <OImage
                        imageSrcUrl={imgSrcUrl ?? ""}
                        alt={poster?.displayImage?.alt}
                      />
                    </AspectRatio>

                    <div className="my-2">
                      <h4 className="leading-5">{poster.name}</h4>
                      <span className="font-thin text-sm">
                        {categoriesPageContent.priceFromLabel}{" "}
                        {market.locale === "se"
                          ? `${poster.price ?? 0} ${market.currencySymbol}`
                          : `${market.currencySymbol}${poster.price ?? 0}`}
                      </span>
                    </div>
                  </article>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    return <NotFoundPage locale={error.data?.locale} />;
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center flex-col">
      <h1>Something went wrong...</h1>
      <h5>Try again in a bit please.</h5>
    </div>
  );
}
